import type { ArtifactStatusEnum } from '@/client/types.gen'
import { CircleAlertIcon } from '@/components/icons/lucide-circle-alert'
import { CircleCheckIcon } from '@/components/icons/lucide-circle-check'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import {
	DOCUMENT_PIPELINE_PHASE_I18N_KEY,
	type DocumentProcessingPipelinePhase,
} from '@/types/documentProcessingPipeline'

export interface ArtifactStatusBadgeProps {
	status: ArtifactStatusEnum
	/** When status is `processing`, show Celery/Step-Function-style sub-state */
	processingPipelinePhase?: DocumentProcessingPipelinePhase
	showSpinner?: boolean
	className?: string
}

type BadgeVisualConfig = {
	textKey: string
	icon: typeof Spinner | typeof CircleCheckIcon | typeof CircleAlertIcon
	spinning: boolean
	className: string
}

const statusConfigMap: Record<ArtifactStatusEnum, BadgeVisualConfig> = {
	pending_upload: {
		textKey: 'pages.projectDetail.artifactStatus.uploading',
		icon: Spinner,
		spinning: true,
		className: 'border-[#8c52ff]/30 bg-[#8c52ff]/10 text-[#8c52ff]',
	},
	processing: {
		textKey: 'pages.projectDetail.artifactStatus.processing',
		icon: Spinner,
		spinning: true,
		className: 'border-[#8c52ff]/30 bg-[#8c52ff]/10 text-[#8c52ff]',
	},
	completed: {
		textKey: 'pages.projectDetail.artifactStatus.complete',
		icon: CircleCheckIcon,
		spinning: false,
		className: 'border-green-200 bg-green-50 text-green-700',
	},
	failed: {
		textKey: 'pages.projectDetail.artifactStatus.failed',
		icon: CircleAlertIcon,
		spinning: false,
		className: 'border-[#ff3131]/30 bg-[#ff3131]/10 text-[#ff3131]',
	},
}

export function ArtifactStatusBadge({
	status,
	processingPipelinePhase,
	showSpinner = true,
	className,
}: ArtifactStatusBadgeProps) {
	const { t } = useTranslation()
	const base = statusConfigMap[status]
	if (!base) {
		return (
			<Badge variant="outline" className={cn(className)}>
				{status}
			</Badge>
		)
	}

	let config: BadgeVisualConfig = base
	if (status === 'processing' && processingPipelinePhase) {
		const isErrorPhase = processingPipelinePhase === 'FAILED' || processingPipelinePhase === 'REVOKED'
		config = {
			textKey: DOCUMENT_PIPELINE_PHASE_I18N_KEY[processingPipelinePhase],
			icon: isErrorPhase ? CircleAlertIcon : Spinner,
			spinning: !isErrorPhase,
			className: isErrorPhase
				? 'border-[#ff3131]/30 bg-[#ff3131]/10 text-[#ff3131]'
				: 'border-[#8c52ff]/30 bg-[#8c52ff]/10 text-[#8c52ff]',
		}
	}

	const Icon = config.icon
	const shouldSpin = config.spinning && showSpinner

	return (
		<Badge
			variant="outline"
			className={cn(config.className, 'gap-1.5', className)}
			aria-label={t(config.textKey)}
		>
			<Icon className={cn('size-3.5 shrink-0', shouldSpin && 'animate-spin')} aria-hidden />
			<span>{t(config.textKey)}</span>
		</Badge>
	)
}
